import { SelectionModel } from '@angular/cdk/collections';
import {
  ChangeDetectorRef,
  ContentChildren,
  Directive,
  ElementRef,
  Input,
  OnDestroy,
  QueryList,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DynamicOrchestrator } from '../orchestrator';
import type { GameDynamicBlockDirective } from './game-dynamic-block.directive';
import { GameDynamicTemplateOutletDirective } from './game-dynamic-template-outlet.directive';
import { isEqual } from 'lodash';

@Directive({
    selector: '[msOrchestratorContainer]',
    standalone: false
})
export class GameDynamicBlockContainerDirective<TContext> implements OnDestroy {
  @Input('msOrchestratorContainer')
  set state(value: string) {
    this._state = value;
    this._orchestrator.stageChanges?.next(value);
  }

  @Input('msOrchestratorContext')
  set context(value: TContext) {
    if (isEqual(value, this._context)) {
      return;
    }

    this._context = value;
    this._orchestrator.contextChanges?.next(value);
  }

  // @ContentChildren(GameDynamicBlockDirective, { descendants: true }) blocks: QueryList<GameDynamicBlockDirective>;

  private readonly blocks: GameDynamicBlockDirective[] = [];

  @ContentChildren(GameDynamicTemplateOutletDirective, { descendants: true })
    templateOutlets: QueryList<GameDynamicTemplateOutletDirective>;

  private _state: string;

  private _context: TContext;

  private readonly _destroyed = new Subject<void>();

  constructor(
    private readonly _cdr: ChangeDetectorRef,
    private readonly _elementRef: ElementRef,
    private readonly _orchestrator: DynamicOrchestrator<TContext>,
  ) {}

  ngAfterViewInit() {
    this._orchestrator.setElementRef(this._elementRef);
    this._orchestrator.onAdjust.pipe(takeUntil(this._destroyed)).subscribe((res) => {
      this.update();
    });
    (this._orchestrator as any).stageChanges.next(this._state);
  }

  ngOnDestroy() {
    this._destroyed.next(void 0);
  }

  addBlock(block: GameDynamicBlockDirective) {
    this.blocks.push(block);
    this.updateBlock(block);
  }

  removeBlock(block: GameDynamicBlockDirective) {
    const index = this.blocks.indexOf(block);
    if (index > -1) {
      this.blocks.splice(index, 1);
    }
  }

  // Optional: Method to access blocks
  getBlocks(): GameDynamicBlockDirective[] {
    return this.blocks;
  }

  private update() {
    this.blocks.forEach((block) => {
      this.updateBlock(block);
    });
  }

  private updateBlock(block: GameDynamicBlockDirective) {
    const config = this._orchestrator.stage[block.name];
    if (config) {
      if (block.elementRef.nativeElement.style) {
        Object.assign(block.elementRef.nativeElement.style, config.styles);
      }
      if (config.func && config.component) {
        block.provideForComponent(config.func, config.component);
      }
      block.cdr.detectChanges();
      this._cdr.detectChanges();
    }
  }
}
